import { Play } from 'lucide-react';

import { MediaFrame, SplitReveal, TiltCard } from '@/components/motion';
import { cn } from '@ibas/ui';

interface Tile {
  /** Site-relative path to the real IBAS photo (also the video poster when `video` is set). */
  image: string;
  /** Optional site-relative campus video — plays muted/looped on view (one tile only). */
  video?: string;
  /** Short hover caption. */
  caption: string;
  /** Meaningful alt text for the underlying media. */
  alt: string;
  /** Bento span classes — base (2-col) and md (4-col) compositions. */
  span: string;
  /** CSS object-position keeping the subject/people in frame under object-cover. */
  position: string;
}

/**
 * The campus/life mosaic tiles — real IBAS photography from the media library;
 * captions describe genuine facets of the Thalwil campus and community.
 */
const TILES: readonly Tile[] = [
  {
    image: '/media/images/campus/ibas-thalwil-building-exterior.webp',
    video: '/media/videos/students-at-ibas.mp4',
    caption: 'Campus, Thalwil',
    alt: 'Exterior view of the copper-panelled IBAS Thalwil campus building.',
    // Full-width square on mobile; a 2×2 square feature on desktop. aspect-square
    // guarantees the 1:1 video never crops; its 2-row height matches two square
    // photo rows exactly.
    span: 'col-span-2 aspect-square lg:row-span-2',
    // Lift slightly so the copper facade and entrance read, not just sky/path.
    position: '50% 40%',
  },
  {
    image: '/media/images/campus/classroom-group-presentation-laptops.webp',
    caption: 'Applied learning',
    alt: 'Students with laptops watching a presentation in an IBAS classroom.',
    span: 'col-span-1 aspect-square',
    // Students and the presenter sit in the upper-middle band.
    position: '50% 40%',
  },
  {
    image: '/media/images/campus/instructor-teaching-classroom-group.webp',
    caption: 'Faculty',
    alt: 'A lecturer in a blazer presenting an operations-research slide to seated students in an IBAS classroom.',
    span: 'col-span-1 aspect-square',
    // Landscape teaching scene fills this wide tile — hold the lecturer and screen.
    position: '50% 45%',
  },
  {
    image: '/media/images/graduation-events/graduation-ceremony-cap-toss-group-photo.webp',
    caption: 'Graduation',
    alt: 'Large group of IBAS graduates in academic gowns throwing their caps in celebration.',
    span: 'col-span-1 aspect-square',
    // Wide group shot — keep the dense crowd centred as the sides crop in.
    position: '50% 50%',
  },
  {
    image: '/media/images/graduation-events/students-lakeside-picnic-zurich.webp',
    caption: 'Global community',
    alt: 'IBAS students share a lakeside picnic table beside Lake Zurich, hills and water behind them.',
    span: 'col-span-1 aspect-square',
    // Landscape: the seated group's faces sit just above centre — hold the band there.
    position: '50% 45%',
  },
  {
    image: '/media/images/campus/campus-lounge-sofas.webp',
    caption: 'Student life',
    alt: 'Lounge area with grey sofas and orange cushions overlooking large windows at the IBAS campus.',
    span: 'col-span-1 aspect-square',
    // Balance the bright windows above with the sofas below.
    position: '50% 45%',
  },
];

export interface MediaMosaicProps {
  className?: string;
  /** Optional id for in-page anchoring. */
  id?: string;
}

/**
 * MediaMosaic — the "Life at IBAS" campus/life media mosaic.
 *
 * Interactivity intent: a responsive bento of MediaFrame tiles, each of which
 * curtain-wipes in on scroll (MediaFrame's built-in ImageReveal) and — on
 * pointer-capable devices — leans in 3D toward the cursor inside a TiltCard, with
 * a soft gold glare. One tile carries a muted, on-view looping campus video. On
 * hover each tile lifts its caption up behind a gold hairline over a navy scrim.
 *
 * Reduced motion / touch: TiltCard renders flat and static, MediaFrame shows the
 * poster (no autoplay) with reveals disabled, and every caption is permanently
 * visible — the grid stays a complete, readable composition with no motion.
 */
export function MediaMosaic({ className, id }: MediaMosaicProps) {
  return (
    <section
      id={id}
      className={cn('flex min-h-[100svh] flex-col justify-center bg-background py-12 md:py-16', className)}
    >
      <div className="mx-auto w-full max-w-6xl px-6">
        <header className="mb-8 max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Life at IBAS
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            A place that moves with you.
          </SplitReveal>
          <p className="mt-4 text-base text-muted-foreground">
            Campus Thalwil — where a global community learns, meets and graduates.
          </p>
        </header>

        {/* Full-width, two-row bento (5 columns on desktop). The campus film is a
            2×2 square feature — a 1:1 (1080×1080) video, so a square frame shows it
            in full with no crop — and its height matches exactly two rows of square
            photo cells. Two rows keeps the whole section inside one screen (≤100vh),
            while spanning the full width removes the empty side margins. The final
            cell carries a small brand tile so all five photos are kept and the
            2-row rectangle completes cleanly. */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5 lg:gap-4">
          {TILES.map((tile) => (
            <div key={tile.caption} data-cursor className={cn('group relative', tile.span)}>
              <TiltCard className="h-full" max={6} glare>
                <MediaFrame
                  image={tile.image}
                  video={tile.video}
                  alt={tile.alt}
                  aspect="h-full"
                  className="h-full"
                  position={tile.position}
                  overlay={14}
                >
                  {/* Bottom navy scrim carries caption contrast over the photo. */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-brand-navy/70 via-brand-navy/15 to-transparent"
                  />

                  {/* Video affordance — only on the moving tile. */}
                  {tile.video ? (
                    <span
                      aria-hidden="true"
                      className="absolute top-4 left-4 flex size-9 items-center justify-center rounded-full bg-brand-navy/70 text-brand-gold-light ring-1 ring-brand-gold/40 backdrop-blur-sm"
                    >
                      <Play className="size-4 translate-x-px" />
                    </span>
                  ) : null}

                  {/* Caption: slides up behind a gold hairline on hover; always
                      visible under reduced motion so nothing is gated on motion. */}
                  <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                    <span className="block h-px w-10 origin-left scale-x-0 bg-brand-gold transition-transform duration-700 ease-out group-hover:scale-x-100 motion-reduce:scale-x-100 motion-reduce:transition-none" />
                    <p className="mt-3 translate-y-2 font-label text-xs tracking-[0.2em] text-white uppercase opacity-0 transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100 motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:transition-none">
                      {tile.caption}
                    </p>
                  </div>
                </MediaFrame>
              </TiltCard>
            </div>
          ))}

          {/* Brand tile — fills the bento's final cell (keeping all five photos and
              completing the two-row rectangle) with a real IBAS line. */}
          <div className="col-span-2 aspect-[2/1] lg:col-span-1 lg:aspect-square">
            <div className="flex h-full w-full flex-col justify-between rounded-2xl bg-brand-navy p-4 md:p-5">
              <span className="font-label text-[0.65rem] tracking-[0.2em] text-brand-gold uppercase">
                Life at IBAS
              </span>
              <p className="font-display text-lg leading-snug text-white md:text-xl">
                Study your way. Connect globally.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
